import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import type { TicketTypeTemplate } from '../../../types/ticketTypeTemplate';
import { FIELD_SX, PRIMARY_BTN_SX } from '../../../styles/designSystem';

interface DuplicateTicketTypeTemplateDialogProps {
  open: boolean;
  source: TicketTypeTemplate | null;
  existingIds: string[];
  onClose: () => void;
  onDuplicate: (newId: string, newName: string) => void;
}

// Seed a collision-free "<id>_copy" candidate from the source slug.
function nextCopyId(baseId: string, existingIds: string[]): string {
  const existing = new Set(existingIds);
  let candidate = `${baseId}_copy`;
  let n = 2;
  while (existing.has(candidate)) {
    candidate = `${baseId}_copy${n++}`;
  }
  return candidate;
}

export const DuplicateTicketTypeTemplateDialog: React.FC<DuplicateTicketTypeTemplateDialogProps> = ({
  open,
  source,
  existingIds,
  onClose,
  onDuplicate,
}) => {
  const [displayName, setDisplayName] = useState('');
  const [ticketTypeId, setTicketTypeId] = useState('');

  useEffect(() => {
    if (open && source) {
      setDisplayName(`${source.displayName} (Copy)`);
      setTicketTypeId(nextCopyId(source.ticketTypeId, existingIds));
    }
    // existingIds is stable for the lifetime of an open dialog; seed only on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source]);

  const trimmedId = ticketTypeId.trim();
  const trimmedName = displayName.trim();
  const isDuplicate = existingIds.includes(trimmedId);
  const isValid = trimmedId.length > 0 && trimmedName.length > 0 && !isDuplicate;

  const handleDuplicate = () => {
    if (!isValid) return;
    onDuplicate(trimmedId, trimmedName);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Duplicate Template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            fullWidth
            autoFocus
            sx={FIELD_SX}
          />
          <TextField
            label="Ticket Type ID"
            value={ticketTypeId}
            onChange={(e) => setTicketTypeId(e.target.value)}
            required
            fullWidth
            helperText={
              isDuplicate ? 'This ID already exists' : 'Must be unique — this is the new template slug.'
            }
            error={isDuplicate}
            slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
            sx={FIELD_SX}
          />
          {isDuplicate && (
            <Alert severity="error">A template with ID "{trimmedId}" already exists.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#888' }}>
          Cancel
        </Button>
        <Button
          onClick={handleDuplicate}
          variant="contained"
          disableElevation
          disabled={!isValid}
          sx={PRIMARY_BTN_SX}
        >
          Duplicate
        </Button>
      </DialogActions>
    </Dialog>
  );
};
